import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { setupAuthInterceptor, retryWithBackoff, deduplicateRequest } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.configuration_api.companies;

// Helper function to configure the Axios instance
const api: AxiosInstance = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Types
export interface Local {
  local_id: number;
  local_name: string;
}

export interface Company {
  company_id: number;
  company_name: string;
  company_rut: string;
  company_email: string;
  company_phone: string;
  admin_name: string;
  address: string;
  state: boolean;
  company_code: string;
  plan_state: boolean;
  city_id: number;
  created_at: string;
  updated_at?: string;
  locales: Local[];
}

export interface CreateCompanyPayload {
  company_name: string;
  address?: string;
  company_email: string;
  company_phone?: string;
}

export interface UpdateCompanyPayload {
  company_name?: string;
  address?: string;
  company_email?: string;
  company_phone?: string;
}

export interface PreviewEmptyCompaniesResponse {
  companies: EmptyCompanyPreview[];
}

export interface EmptyCompanyPreview {
  company_id: number;
  company_name: string;
  company_email: string;
  created_at: string | null;
  dependencies: {
    providers: number;
    provider_categories: number;
    users: number;
    storage_units: number;
    account_creation_codes: number;
  };
}

export interface CleanEmptyCompaniesResponse {
  deleted_count: number;
}

// Get all companies
export const GetAllCompanies = async (): Promise<AxiosResponse<Company[]>> => {
  return await deduplicateRequest(
    'GetAllCompanies',
    () => retryWithBackoff(() => api.get<Company[]>('/GetAllCompanies'))
  );
};

// Create a new company
export const CreateCompany = async (payload: CreateCompanyPayload): Promise<AxiosResponse<Company>> => {
  return await retryWithBackoff(() => api.post<Company>('/CreateCompany', payload));
};

// Create company registration (no auth required)
export const CreateCompanyRegistration = async (payload: CreateCompanyPayload): Promise<AxiosResponse<Company>> => {
  return await retryWithBackoff(() => api.post<Company>('/CreateCompanyRegistration', payload));
};

// Update a company
export const UpdateCompany = async (companyId: number, payload: UpdateCompanyPayload): Promise<AxiosResponse<Company>> => {
  return await retryWithBackoff(() => api.put<Company>(`/UpdateCompany/${companyId}`, payload));
};

// Delete a company
export const DeleteCompany = async (companyId: number): Promise<AxiosResponse<void>> => {
  return await retryWithBackoff(() => api.delete<void>(`/DeleteCompany/${companyId}`));
};

// Delete company registration
export const DeleteCompanyRegistration = async (code: string): Promise<AxiosResponse<void>> => {
  return await retryWithBackoff(() => api.delete<void>(`/DeleteCompanyRegistration/${code}`));
};

// Preview empty companies (GET)
export const PreviewEmptyCompanies = async (): Promise<AxiosResponse<PreviewEmptyCompaniesResponse>> => {
  return await retryWithBackoff(() => api.get<PreviewEmptyCompaniesResponse>('/CleanEmptyCompanies'));
};

// Clean/Delete empty companies (DELETE)
export const CleanEmptyCompanies = async (): Promise<AxiosResponse<CleanEmptyCompaniesResponse>> => {
  return await retryWithBackoff(() => api.delete<CleanEmptyCompaniesResponse>('/CleanEmptyCompanies'));
};

// Validate company email by ID
export const ValidateCompanyEmailById = async (companyId: number): Promise<AxiosResponse<{ valid: boolean }>> => {
  return await retryWithBackoff(() => api.get<{ valid: boolean }>(`/ValidateCompanyEmailById/${companyId}`));
};

// Update company name
export const UpdateCompanyName = async (companyId: number, companyName: string): Promise<AxiosResponse<Company>> => {
  return await retryWithBackoff(() => api.put<Company>(`/UpdateCompanyName/${companyId}`, { company_name: companyName }));
};

// Update company address
export const UpdateCompanyAddress = async (companyId: number, address: string): Promise<AxiosResponse<Company>> => {
  return await retryWithBackoff(() => api.put<Company>(`/UpdateCompanyAddress/${companyId}`, { address }));
};

// Update company email
export const UpdateCompanyEmail = async (companyId: number, companyEmail: string): Promise<AxiosResponse<Company>> => {
  return await retryWithBackoff(() => api.put<Company>(`/UpdateCompanyEmail/${companyId}`, { company_email: companyEmail }));
};

// Update company phone
export const UpdateCompanyPhone = async (companyId: number, companyPhone: string): Promise<AxiosResponse<Company>> => {
  return await retryWithBackoff(() => api.put<Company>(`/UpdateCompanyPhone/${companyId}`, { company_phone: companyPhone }));
};

// Toggle company state (active/inactive)
export const ToggleCompanyState = async (companyId: number): Promise<AxiosResponse<Company>> => {
  return await retryWithBackoff(() => api.put<Company>(`/ToggleCompanyState/${companyId}`, {}));
};